import React, { useState } from "react";
import CustomModal from "../../../components/Modal/CustomModal";
import { getFromLocalStorage } from "../../../utils/common-utils";
import { createCountryOperator } from "../../../api/Api";
import Swal from "sweetalert2";
import Select from 'react-select';
import moment from 'moment-timezone'; // for timezone options

const AddCountryOperatorModal = ({ show, onHide, onSuccess }) => {
  // Get all timezones for the select options
  const timezoneOptions = moment.tz.names().map(tz => ({
    value: tz,
    label: tz
  }));

  const [formData, setFormData] = useState({
    countryName: "",
    countryShortName: "",
    timeZone: null,
    msisdnPrefix: "",
    msisdnLength: "",
    mnc: "",
    mcc: "",
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Handle number-only fields
    if (["mnc", "mcc", "msisdnLength", "msisdnPrefix"].includes(name)) {
      // Only allow numbers
      if (!/^\d*$/.test(value)) return;

      // Check max lengths
      if (name === "mnc" && value.length > 5) return;
      if (name === "mcc" && value.length > 4) return;
      if (name === "msisdnPrefix" && value.length > 5) return;
      if (name === "msisdnLength" && value.length > 2) return; // Allow up to 2 digits for length input
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleTimeZoneChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      timeZone: selectedOption.value
    }));
    if (errors.timeZone) {
      setErrors(prev => ({
        ...prev,
        timeZone: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations
    if (!formData.countryName.trim()) newErrors.countryName = "Country name is required";
    if (!formData.countryShortName.trim()) newErrors.countryShortName = "Country short name is required";
    if (!formData.timeZone) newErrors.timeZone = "Timezone is required";
    if (!formData.msisdnPrefix) newErrors.msisdnPrefix = "MSISDN prefix is required";
    if (!formData.msisdnLength) newErrors.msisdnLength = "MSISDN length is required";
    if (!formData.mnc) newErrors.mnc = "MNC is required";
    if (!formData.mcc) newErrors.mcc = "MCC is required";

    // Length validations
    if (formData.mnc) {
      const mncLength = formData.mnc.length;
      if (mncLength !== 3) {
        newErrors.mnc = "MNC must be 3 digits";
      }
    }

    if (formData.mcc) {
      const mccLength = formData.mcc.length;
      if (mccLength !== 2) {
        newErrors.mcc = "MCC must be 2 digits";
      }
    }

    if (formData.msisdnLength) {
      const msisdnLengthNum = Number(formData.msisdnLength);
      if (msisdnLengthNum < 6) {
        newErrors.msisdnLength = "MSISDN length must be at least 6 digits";
      }
    }

    if (formData.msisdnPrefix && formData.msisdnPrefix.length > 5) {
      newErrors.msisdnPrefix = "MSISDN prefix cannot exceed 5 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      countryName: "",
      countryShortName: "",
      timeZone: null,
      msisdnPrefix: "",
      msisdnLength: "",
      mnc: "",
      mcc: "",
    });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onHide?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const payload = {
        ...formData,
        country: formData.countryShortName, // Add country field with countryShortName value
        userId: getFromLocalStorage("userData")?.id
      };

      const response = await createCountryOperator(payload);

      if (response?.statusDescription?.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Country Operator added successfully'
        });
        onSuccess?.();
        handleClose();
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to add country operator'
      });
    }
  };

  return (
      <CustomModal
        show={show}
        onHide={handleClose}
        title="Add Country Operator"
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <div className="row w-100 m-0">
          {/* <div className="row"> */}
            <div className="form-group col-sm-4">
              {/* <div className="form-group"> */}
                <label className="">Country Name<span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.countryName ? 'is-invalid' : ''}`}
                  name="countryName"
                  value={formData.countryName}
                  onChange={handleInputChange}
                  placeholder="Enter country name"
                />
                {errors.countryName && <div className="invalid-feedback">{errors.countryName}</div>}
              {/* </div> */}
            </div>
            <div className="form-group col-sm-4">
              {/* <div className="form-group"> */}
                <label className="">Country Short Name<span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.countryShortName ? 'is-invalid' : ''}`}
                  name="countryShortName"
                  value={formData.countryShortName}
                  onChange={handleInputChange}
                  placeholder="Enter country short name"
                />
                {errors.countryShortName && <div className="invalid-feedback">{errors.countryShortName}</div>}
              {/* </div> */}
            </div>
          {/* </div> */}

          {/* <div className="row mt-3"> */}
            <div className="form-group col-sm-4">
              {/* <div className="form-group"> */}
                <label className="">Time Zone<span className="text-danger">*</span></label>
                <Select
                  options={timezoneOptions}
                  value={timezoneOptions.find(tz => tz.value === formData.timeZone)}
                  onChange={handleTimeZoneChange}
                  className={errors.timeZone ? 'is-invalid' : ''}
                  placeholder="Select timezone"
                />
                {errors.timeZone && <div className="invalid-feedback d-block">{errors.timeZone}</div>}
              {/* </div> */}
            </div>
            <div className="form-group col-sm-4">
              {/* <div className="form-group"> */}
                <label className="">MSISDN Prefix<span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.msisdnPrefix ? 'is-invalid' : ''}`}
                  name="msisdnPrefix"
                  value={formData.msisdnPrefix}
                  onChange={handleInputChange}
                  placeholder="Enter MSISDN prefix (max 5 digits)"
                  maxLength={5}
                />
                {errors.msisdnPrefix && <div className="invalid-feedback">{errors.msisdnPrefix}</div>}
                {/* <small className="text-muted">Numbers only, maximum 5 digits</small> */}
              {/* </div> */}
            </div>
          {/* </div> */}

          {/* <div className="row mt-3"> */}
            <div className="form-group col-sm-4">
              {/* <div className="form-group"> */}
                <label className="">MSISDN Length<span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.msisdnLength ? 'is-invalid' : ''}`}
                  name="msisdnLength"
                  value={formData.msisdnLength}
                  onChange={handleInputChange}
                  placeholder="Enter MSISDN length (min 6 digits)"
                  maxLength={2} // Allow up to 2 characters for the length input
                />
                {errors.msisdnLength && <div className="invalid-feedback">{errors.msisdnLength}</div>}
                {/* <small className="text-muted">Must be at least 6 digits</small> */}
              {/* </div> */}
            </div>
            <div className="form-group col-sm-4">
              {/* <div className="form-group"> */}
                <label className="">MNC<span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.mnc ? 'is-invalid' : ''}`}
                  name="mnc"
                  value={formData.mnc}
                  onChange={handleInputChange}
                  placeholder="Enter 3 digits MNC"
                  maxLength={3}
                />
                {errors.mnc && <div className="invalid-feedback">{errors.mnc}</div>}
                {/* <small className="text-muted">Must be between 3 and 5 digits</small> */}
              {/* </div> */}
            </div>
          {/* </div> */}

          {/* <div className="row mt-3"> */}
            <div className="form-group col-sm-4">
              {/* <div className="form-group"> */}
                <label className="">MCC<span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-control ${errors.mcc ? 'is-invalid' : ''}`}
                  name="mcc"
                  value={formData.mcc}
                  onChange={handleInputChange}
                  placeholder="Enter 2 digits MCC"
                  maxLength={2}
                />
                {errors.mcc && <div className="invalid-feedback">{errors.mcc}</div>}
                {/* <small className="text-muted">Must be between 2 and 4 digits</small> */}
              {/* </div> */}
            </div>
          {/* </div> */}
          </div>

          <div className="modal-footer pb-0 border-0 d-flex justify-content-between px-0 mx-0 buttons_divs">
            <button
              type="button"
              className="btn btn-secondary m-0"
              onClick={handleClose}
            >
              Close
            </button>
            <button
              type="submit"
              className="btn btn-primary m-0"
            >
              Submit
            </button>
          </div>
        </form>
      </CustomModal>
  );
};

export default AddCountryOperatorModal;
